import { useCallback, useMemo, useState } from 'react';
import {
  capitalize,
  distinctArrayBy,
} from '@elementary/common-ui/utils/common';
import { localizeDate } from '@elementary/common-ui/utils/datetime';
import { TEST_RESULTS } from '@elementary/common-ui/constants/queryKeys';
import useGraphqlPagedQuery from '@elementary/common-ui/hooks/useGraphqlPagedQuery';
import {
  DEFAULT_DAYS_BACK,
  EXPORT_PAGE_SIZE,
  PAGE_SIZE,
} from '@elementary/common-ui/constants';
import {
  parseApiTestResultTableData,
  type ApiTestResultsTableData,
  type TestResultsTableData,
} from '@elementary/common-ui/components';
import { useCSVExport } from 'shared/hooks/useCSVExport';
import {
  ApiTestResultsTableDataPagedResponse,
  getTestResultsTableDataQuery,
} from './api';
import useTestResultsQueryContext from './useTestResultsQueryContext';

const TestResultsCsvHeaders: {
  id: keyof TestResultsTableData;
  title: string;
}[] = [
  { id: 'databaseName', title: 'Database' },
  { id: 'schemaName', title: 'Schema' },
  { id: 'tableName', title: 'Table name' },
  { id: 'columnName', title: 'Column name' },
  { id: 'testName', title: 'Test name' },
  { id: 'testSubType', title: 'Test type' },
  { id: 'sourceType', title: 'Source' },
  { id: 'qualityDimension', title: 'Quality dimension' },
  { id: 'testTags', title: 'Test tags' },
  { id: 'testOwners', title: 'Test owners' },
  { id: 'failedRows', title: 'Failed rows' },
  { id: 'description', title: 'Description' },
  { id: 'latestRunTime', title: 'Last test run' },
  { id: 'latestRunStatus', title: 'Last status' },
  { id: 'assetTags', title: 'Asset tags' },
  { id: 'assetOwners', title: 'Asset owners' },
  { id: 'assetLastDataModificationTime', title: 'Last table update' },
  { id: 'assetMaterialization', title: 'Asset materialization' },
  { id: 'criticalAsset', title: 'Critical Asset' },
  { id: 'assetName', title: 'Asset name' },
  { id: 'assetPath', title: 'Asset path' },
  { id: 'assetSubscribers', title: 'Asset subscribers' },
];

const mapTestResultToCsvRow = (row: TestResultsTableData) => ({
  ...row,
  testTags: row.testTags?.join(', ') ?? '',
  testOwners: row.testOwners?.join(', ') ?? '',
  assetTags: row.assetTags?.join(', ') ?? '',
  assetOwners: row.assetOwners?.join(', ') ?? '',
  assetSubscribers: row.assetSubscribers?.join(', ') ?? '',
  failedRows: row.failedRows ?? '',
  latestRunTime: row.latestRunTime
    ? localizeDate(row.latestRunTime).format('L LTS')
    : '',
  assetLastDataModificationTime: row.assetLastDataModificationTime
    ? localizeDate(row.assetLastDataModificationTime).format('L LTS')
    : '',
  assetMaterialization: row.assetMaterialization
    ? capitalize(row.assetMaterialization)
    : '',
  criticalAsset:
    row.criticalAsset === undefined ? '' : row.criticalAsset ? 'Yes' : 'No',
});

const useTestResults = ({
  enabled = true,
  onError,
  search,
}: {
  enabled?: boolean;
  onError?: () => void;
  search?: string;
}) => {
  const {
    envid,
    selectedAssetIds,
    selectedTestIds,
    selectedIdsHash,
    hasSelectedNodes,
    urlFiltersDebounced,
    testExecutionFilters,
    urlSort,
    sort,
    filterOptions,
    isFilterOptionsFetching,
  } = useTestResultsQueryContext({ enabled });

  const { exportToCSV } = useCSVExport({ headers: TestResultsCsvHeaders });
  const [isExporting, setIsExporting] = useState(false);

  const {
    data,
    isLoading,
    isFetching,
    isError,
    fetchNextPage,
    hasNextPage,
    fetchAllPages,
  } = useGraphqlPagedQuery<
    ApiTestResultsTableData,
    ApiTestResultsTableDataPagedResponse
  >({
    queryKey: [
      TEST_RESULTS,
      envid,
      selectedIdsHash,
      DEFAULT_DAYS_BACK,
      urlFiltersDebounced,
      urlSort,
      search,
    ],
    query: getTestResultsTableDataQuery,
    variables: {
      testIds: selectedTestIds.length ? selectedTestIds : null,
      assetIds: selectedAssetIds.length ? selectedAssetIds : null,
      daysBack: DEFAULT_DAYS_BACK,
      testExecutionFilters,
      sort,
      search: search == '' ? null : search,
      limit: PAGE_SIZE,
    },
    select: (data) => data.latestTestExecutionsPaged,
    onError: onError,
    enabled: enabled && hasSelectedNodes,
    envId: envid,
  });

  const tableData = useMemo<TestResultsTableData[]>(() => {
    const distinctApiTests = distinctArrayBy(data, 'execution.subTestUniqueId');
    return distinctApiTests.map(parseApiTestResultTableData);
  }, [data]);

  const onCSVDownload = useCallback(async () => {
    setIsExporting(true);
    try {
      const allResults = await fetchAllPages({ limit: EXPORT_PAGE_SIZE });
      const csvData = distinctArrayBy(allResults, 'execution.subTestUniqueId')
        .map(parseApiTestResultTableData)
        .map(mapTestResultToCsvRow);
      exportToCSV(csvData, 'test-results.csv');
    } finally {
      setIsExporting(false);
    }
  }, [fetchAllPages, exportToCSV]);

  return {
    tableData,
    filterOptions,
    isFetching: isLoading || isFetching,
    isFilterOptionsFetching,
    isError,
    fetchNextPage,
    hasNextPage,
    onCSVDownload,
    isExporting,
  };
};

export default useTestResults;
